import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, User, FileText, Eye, ArrowLeft, Mail, Loader2, History, Download } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { getPayrollRuns, getPayrollRunDetails, PayrollRun, PayrollRunDetails, Paystub } from '../../lib/payrollRuns';
import { getPayrollPeriodSettings } from '../../lib/payrollPeriodSettings';
import { downloadPayrollBreakdownForRun, resendPayrollBreakdownForRun } from '../../lib/payrollAccountantEmail';
import PaystubView from './PaystubView';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { payrollMinutesFromHours } from '../../lib/payrollHoursLabel';

function runStatusLabel(status: string): string {
  if (status === 'voided') return 'Voided';
  if (status === 'completed') return 'Completed';
  if (status === 'cancelled') return 'Cancelled';
  return status;
}

function runStatusClass(status: string): string {
  if (status === 'voided') return 'bg-gray-100 text-gray-600';
  if (status === 'completed') return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
}

interface PayrollHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
}

export default function PayrollHistoryModal({
  isOpen,
  onClose,
  businessId
}: PayrollHistoryModalProps) {
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<PayrollRunDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedPaystubs, setExpandedPaystubs] = useState<Set<string>>(new Set());
  const [viewingPaystub, setViewingPaystub] = useState<{
    paystub: Paystub;
    resourceName: string;
    resourceEmail: string | null;
  } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [accountantEmail, setAccountantEmail] = useState<string | null>(null);
  const [isSendingToAccountant, setIsSendingToAccountant] = useState(false);
  const [isDownloadingBreakdown, setIsDownloadingBreakdown] = useState(false);
  const historyMinutes = (selectedRun?.items ?? []).flatMap((item) => [
    payrollMinutesFromHours(item.total_hours || 0),
    Number(item.overtime_minutes) || 0,
  ]);
  const hoursText = usePayrollHoursText(historyMinutes);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      setSelectedRun(null);
      setExpandedPaystubs(new Set());
      setViewingPaystub(null);
    }, 200);
  };

  useEffect(() => {
    if (isOpen) {
      loadRuns();
      void loadAccountantEmail();
    }
  }, [isOpen, businessId]);

  const loadAccountantEmail = async () => {
    if (!businessId) {
      setAccountantEmail(null);
      return;
    }
    try {
      const settings = await getPayrollPeriodSettings(businessId);
      setAccountantEmail(settings?.accountant_email?.trim() || null);
    } catch {
      setAccountantEmail(null);
    }
  };

  const loadRuns = async () => {
    try {
      setIsLoading(true);
      const data = await getPayrollRuns(businessId);
      setRuns(data);
    } catch (error) {
      console.error('Error loading payroll runs:', error);
      toast.error('Failed to load payroll history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (runId: string) => {
    try {
      const details = await getPayrollRunDetails(runId);
      setSelectedRun(details);
    } catch (error) {
      console.error('Error loading run details:', error);
      toast.error('Failed to load run details');
    }
  };

  const handleViewPaystub = (resourceId: string) => {
    if (!selectedRun) return;
    const item = selectedRun.items.find(i => i.resource_id === resourceId);
    const paystub = selectedRun.paystubs.find(p => p.resource_id === resourceId);
    if (item && paystub) {
      setViewingPaystub({
        paystub,
        resourceName: item.resource_name,
        resourceEmail: item.resource_email
      });
    }
  };

  const handleDownloadBreakdown = async () => {
    if (!selectedRun || isDownloadingBreakdown) return;

    setIsDownloadingBreakdown(true);
    const loadingToast = toast.loading('Preparing payroll breakdown...');

    try {
      const result = await downloadPayrollBreakdownForRun(businessId, selectedRun);
      toast.dismiss(loadingToast);
      if (result.cancelled) {
        // The share sheet was dismissed. Nothing was saved.
      } else if (result.success) {
        toast.success('Payroll breakdown downloaded');
      } else {
        toast.error(result.error || 'Failed to download payroll breakdown');
      }
    } catch (error) {
      console.error('Error downloading payroll breakdown:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to download payroll breakdown');
    } finally {
      setIsDownloadingBreakdown(false);
    }
  };

  const handleSendToAccountant = async () => {
    if (!selectedRun || !accountantEmail) return;

    setIsSendingToAccountant(true);
    const loadingToast = toast.loading('Sending payroll breakdown to accountant...');

    try {
      const result = await resendPayrollBreakdownForRun(businessId, accountantEmail, selectedRun);

      toast.dismiss(loadingToast);
      if (result.success) {
        toast.success('Payroll breakdown sent to accountant');
      } else {
        toast.error(result.error || 'Failed to send to accountant');
      }
    } catch (error) {
      console.error('Error sending to accountant:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to send to accountant');
    } finally {
      setIsSendingToAccountant(false);
    }
  };

  const togglePaystub = (resourceId: string) => {
    const newExpanded = new Set(expandedPaystubs);
    if (newExpanded.has(resourceId)) {
      newExpanded.delete(resourceId);
    } else {
      newExpanded.add(resourceId);
    }
    setExpandedPaystubs(newExpanded);
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 transition-opacity duration-200 ${
            isVisible ? 'bg-black/50' : 'bg-black/0'
          }`}
          style={{
            zIndex: 100200,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onClick={handleClose}
          onTransitionEnd={handleTransitionEnd}
        >
          <div
            className={`fixed inset-y-0 right-0 flex w-full max-w-full flex-col bg-white shadow-2xl transition-transform duration-200 sm:w-[560px] ${
              isVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100210,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex h-full flex-col overflow-hidden">
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200/60 bg-white px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  {selectedRun ? (
                    <button
                      type="button"
                      onClick={() => { setSelectedRun(null); setExpandedPaystubs(new Set()); }}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50">
                      <History className="h-5 w-5 text-blue-600" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="truncate text-[17px] font-semibold text-gray-900">
                      {selectedRun ? `Run #${selectedRun.run_number.toString().padStart(4, '0')}` : 'Payroll History'}
                    </h2>
                    <p className="mt-0.5 truncate text-[13px] text-gray-500">
                      {selectedRun
                        ? `${format(new Date(selectedRun.period_start_date), 'MMM d')} – ${format(new Date(selectedRun.period_end_date), 'MMM d, yyyy')}`
                        : 'Completed and voided runs'
                      }
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div
                className="flex-1 overflow-y-auto bg-gray-50"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div className="p-4">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                      <span className="ml-3 text-gray-600">Loading payroll history...</span>
                    </div>
                  ) : selectedRun ? (
                    /* Run Details View */
                    <div className="space-y-5">
                      {/* Run Summary */}
                      <div className="bg-blue-50 rounded-[12px] border border-blue-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-blue-900">Summary</span>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${runStatusClass(selectedRun.status)}`}>
                            {runStatusLabel(selectedRun.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-blue-600">Total Amount</p>
                            <p className="text-lg font-bold text-blue-900 mt-0.5">${selectedRun.total_amount.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Resources</p>
                            <p className="text-lg font-bold text-blue-900 mt-0.5">{selectedRun.total_resources}</p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600">Run Date</p>
                            <p className="text-sm font-semibold text-blue-900 mt-0.5">{format(new Date(selectedRun.run_at), 'MMM d, yyyy')}</p>
                          </div>
                        </div>

                        {selectedRun.status === 'voided' && (
                          <p className="mt-3 text-[13px] text-blue-800">Hours in this period were stamped voided and were not paid.</p>
                        )}

                        {selectedRun.status !== 'voided' ? (
                          <div className="mt-4 pt-4 border-t border-blue-200/60">
                            {accountantEmail ? (
                              <>
                                <p className="text-xs text-blue-600 mb-2">Accountant</p>
                                <p className="text-sm text-blue-900 truncate mb-3">{accountantEmail}</p>
                              </>
                            ) : null}
                            <div className={`grid gap-2 ${accountantEmail ? 'grid-cols-2' : 'grid-cols-1'}`}>
                              <button
                                type="button"
                                onClick={() => void handleDownloadBreakdown()}
                                disabled={isDownloadingBreakdown || isSendingToAccountant}
                                className="py-3 px-3 bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                {isDownloadingBreakdown ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Downloading...
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-4 w-4" />
                                    Download
                                  </>
                                )}
                              </button>
                              {accountantEmail ? (
                                <button
                                  type="button"
                                  onClick={() => void handleSendToAccountant()}
                                  disabled={isSendingToAccountant || isDownloadingBreakdown}
                                  className="py-3 px-3 bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {isSendingToAccountant ? (
                                    <>
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <Mail className="h-4 w-4" />
                                      Send to Accountant
                                    </>
                                  )}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {/* Employee Paystubs */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Employee Paystubs</h4>
                        <div className="space-y-3">
                          {selectedRun.items.map((item) => (
                            <div key={item.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                              <button
                                onClick={() => togglePaystub(item.resource_id)}
                                className="w-full p-4 hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] transition-all duration-150 flex items-center justify-between touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 rounded-[10px]">
                                    <User className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div className="text-left">
                                    <p className="font-semibold text-gray-900 text-[14px]">{item.resource_name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{item.resource_email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <p className="font-bold text-gray-900">${item.total_earnings.toFixed(2)}</p>
                                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${expandedPaystubs.has(item.resource_id) ? 'rotate-180' : ''}`} />
                                </div>
                              </button>

                              {expandedPaystubs.has(item.resource_id) && (
                                <div className="px-4 pb-4 pt-0 space-y-3 border-t border-gray-100">
                                  <div className="pt-3" />
                                  <div className="grid grid-cols-2 gap-3">
                                    {item.total_hours !== null && item.total_hours > 0 && (
                                      <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                        <p className="text-xs text-gray-500">Total Hours</p>
                                        <p className="text-sm font-bold text-gray-900 mt-0.5">{hoursText(payrollMinutesFromHours(item.total_hours || 0))}</p>
                                      </div>
                                    )}
                                    {(Number(item.overtime_minutes) || 0) > 0 && (
                                      <div className="bg-orange-50 p-3 rounded-xl border border-orange-200/60">
                                        <p className="text-xs text-orange-700">Overtime (included)</p>
                                        <p className="text-sm font-bold text-gray-900 mt-0.5">
                                          {hoursText(Number(item.overtime_minutes) || 0)}
                                          {(Number(item.overtime_earnings) || 0) > 0
                                            ? ` · $${Number(item.overtime_earnings).toFixed(2)}`
                                            : ''}
                                        </p>
                                      </div>
                                    )}
                                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/60">
                                      <p className="text-xs text-gray-500">Compensation</p>
                                      <p className="text-sm font-bold text-gray-900 mt-0.5">{item.compensation_type.replace('_', ' ').toUpperCase()}</p>
                                    </div>
                                  </div>

                                  {/* Earnings Breakdown */}
                                  <div className="pt-2">
                                    <p className="text-xs font-semibold text-gray-600 mb-2">Earnings Breakdown</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {item.timeclock_earnings > 0 && (
                                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200/60">
                                          <p className="text-xs text-gray-600 mb-0.5">Timeclock ({item.timeclock_count})</p>
                                          <p className="text-sm font-bold text-gray-900">${item.timeclock_earnings.toFixed(2)}</p>
                                          {(Number(item.overtime_minutes) || 0) > 0 && (
                                            <p className="text-[11px] font-semibold text-orange-600 mt-0.5">
                                              incl. {hoursText(Number(item.overtime_minutes) || 0)} OT
                                            </p>
                                          )}
                                        </div>
                                      )}
                                      {item.flat_rate_earnings > 0 && (
                                        <div className="bg-green-50 rounded-xl p-3 border border-green-200/60">
                                          <p className="text-xs text-green-700 mb-0.5">Flat Rate ({item.flat_rate_count})</p>
                                          <p className="text-sm font-bold text-gray-900">${item.flat_rate_earnings.toFixed(2)}</p>
                                        </div>
                                      )}
                                      {item.commission_earnings > 0 && (
                                        <div className="bg-orange-50 rounded-xl p-3 border border-orange-200/60">
                                          <p className="text-xs text-orange-700 mb-0.5">Commission ({item.commission_count})</p>
                                          <p className="text-sm font-bold text-gray-900">${item.commission_earnings.toFixed(2)}</p>
                                        </div>
                                      )}
                                      {item.bonus_earnings > 0 && (
                                        <div className="bg-pink-50 rounded-xl p-3 border border-pink-200/60">
                                          <p className="text-xs text-pink-700 mb-0.5">Bonus ({item.bonus_count})</p>
                                          <p className="text-sm font-bold text-gray-900">${item.bonus_earnings.toFixed(2)}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {selectedRun?.status !== 'voided' && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleViewPaystub(item.resource_id); }}
                                      className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] text-white rounded-xl font-semibold text-sm transition-all duration-150 flex items-center justify-center gap-2 touch-manipulation"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <Eye className="h-4 w-4" />
                                      View Paystub
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : runs.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payroll Runs Yet</h3>
                      <p className="text-sm text-gray-500 max-w-xs mx-auto">Run your first payroll to see it here</p>
                    </div>
                  ) : (
                    /* History List View */
                    <div className="space-y-3">
                      {runs.map((run) => (
                        <div
                          key={run.id}
                          className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-all duration-150"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 rounded-[10px]">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 text-[14px]">
                                  Run #{run.run_number.toString().padStart(4, '0')}
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {format(new Date(run.run_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${runStatusClass(run.status)}`}>
                              {runStatusLabel(run.status)}
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-3 mb-3">
                            <div>
                              <p className="text-xs text-gray-500">Period</p>
                              <p className="text-sm font-semibold text-gray-900 mt-0.5">
                                {format(new Date(run.period_start_date), 'MMM d')} – {format(new Date(run.period_end_date), 'MMM d')}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Total</p>
                              <p className="text-sm font-bold text-gray-900 mt-0.5">${run.total_amount.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Resources</p>
                              <p className="text-sm font-semibold text-gray-900 mt-0.5">{run.total_resources}</p>
                            </div>
                          </div>

                          <button
                            onClick={() => handleViewDetails(run.id)}
                            className="w-full py-3 px-4 bg-blue-50 hover:bg-blue-100 active:bg-blue-150 active:scale-[0.98] text-blue-600 rounded-xl text-sm font-semibold transition-all duration-150 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            View Details
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {viewingPaystub && (
        <PaystubView
          paystub={viewingPaystub.paystub}
          resourceInfo={{
            name: viewingPaystub.resourceName,
            email: viewingPaystub.resourceEmail
          }}
          runNumber={selectedRun?.run_number || 0}
          onClose={() => setViewingPaystub(null)}
        />
      )}
    </>
  );
}
